
import Vue from "vue";
import { mapActions } from "vuex";
import SingleSelect from "../form/SingleSelect.vue";
import avsCheck from "~/assets/utils/Avs/avsCheck";
import mapState from "~/assets/utils/mapState";
import parseStateLabel from "~/assets/utils/parseStateLabel";
import { DetailsSource, AVSStatus } from "~/entities/AVS";
import { Details } from "~/entities/Details";
import Place from "~/entities/Place";
import { Country, State } from "~/entities/Response/FormData";
import SavedAddress from "~/entities/SavedAddress";

export default Vue.extend({
  components: {
    SingleSelect,
  },
  props: {
    validated: Boolean,
    label: {
      type: String,
      default: "",
    },
    googleObj: {
      type: Object,
      default: null,
    },
    preSelection: {
      type: String,
      default: "",
    },
    errors: {
      type: Boolean,
      default: false,
    },
    equal: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as () => Array<{ id: string; label: string }>,
      default: () => [],
    },
    userAddresses: {
      type: Array as () => Array<SavedAddress>,
      default: () => [],
    },
    id: {
      type: Number,
      default: 0,
    },
    direction: {
      type: String,
      default: "from",
    },
    allowedCountries: {
      type: Array<Country>,
      default: () => [],
    },
    states: {
      type: Array<State>,
      default: () => [],
    },
    warning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      autocomplete: {} as google.maps.places.Autocomplete,
      location: null as null | string,
      city: null as null | string,
      state: null as any,
      country: null as any,
      postalCode: null as null | string,
      unit: null as null | string,
      isSingle: true as boolean,
      isOpen: false as boolean,
      show: false as boolean,
      disabled: false,
      place: {} as Place,
      processing: false,
      isSavedAddress: false,
      skipEmitCheck: false,
      suggestions: [] as google.maps.places.AutocompletePrediction[],
      highlightedIndex: -1,
      filteredUserAddress: [] as SavedAddress[],
      isOpenSuggestions: false,
      isAddressExpanded: false,
      searchInputValue: "" as string,
      showArrowBtn: false as boolean,
      needSetAddress: true as boolean,
      isLimit: false as boolean,
      isLoadingInput: false as boolean,
      allowedState: [] as any,
      filterStates: [] as any,
    };
  },
  computed: {
    placeholder(): string {
      return this.label;
    },
    hasAdvancedInStep(): boolean {
      return this.$store.state.ship.hasAdvancedInStep;
    },
  },
  watch: {
    hasAdvancedInStep(newVal) {
      if (newVal) {
        if (this.hasAdvancedInStep) {
          this.splitAddress();
        }
        this.isSingle = false;
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
      }
    },
    country(newVal) {
      if (newVal) {
        this.allowedState = this.filterStates[newVal.code];
      }
    },
    isLoadingInput(newVal) {
      this.$emit("emitLoadingInput", newVal);
    },
  },
  mounted() {
    if (this.preSelection) {
      this.searchInputValue = this.preSelection;
    }
    if (this.hasAdvancedInStep) {
      this.splitAddress();
      this.filterStates = mapState(this.states);
      this.allowedState = this.filterStates[this.country?.code];
    } else if(this.$store.state.ship.stages.trips?.[this.id]?.[this.direction]){
      if (this.$store.state.ship.stages.trips[this.id][this.direction].formatted_address) {
        this.location = this.$store.state.ship.stages.trips[this.id][this.direction].formatted_address
      }
    }
  },
  methods: {
    ...mapActions({
      requestResolve: "ship/requestResolve",
    }),
    async selectEventAddress(item: any): Promise<void> {
      if (item) {
        this.isLoadingInput = true;
        this.needSetAddress = false;
        this.show = true;
        this.isOpen = false;
        this.disabled = true;
        this.isOpenSuggestions = false;
      }
      try {
        await this.resolveEventLocation(item);
        this.disabled = false;
      } catch (error) {
        this.disabled = false;
      }
    },
    async selectUserAddress(item: any): Promise<void> {
      if (item) {
        this.isLoadingInput = true;
        this.needSetAddress = false;
        this.show = true;
        this.isOpen = false;
        this.disabled = true;
        this.isOpenSuggestions = false;
      }
      try {
        await this.resolveUserLocation(item);
        this.disabled = false;
      } catch (error) {
        this.disabled = false;
      }
    },
    onShow(): void {
      this.isAddressExpanded = !this.isAddressExpanded;
    },
    close(): void {
      this.isOpen = false;
      this.suggestions = [];
      this.isOpenSuggestions = false;
    },
    async ResolveLocation(): Promise<void> {
      try {
        const resolvedLocation = await this.requestResolve({
          address: this.place?.address_components,
          originalAddress: this.place.formatted_address,
          company: this.place.name,
          type: 1,
        });

        if (resolvedLocation && !resolvedLocation.forLocation) {
          const result =
            this.place.reverseResolvedAddressComponents(resolvedLocation);
          if (result && result.changes && !this.isSavedAddress) {
            const newDetails = new Details();
            const allowedStates = mapState(this.states);
            newDetails.setStage(
              this.place,
              this.allowedCountries,
              allowedStates
            );
            newDetails.searchType = "full";
            const countryCode =
              typeof newDetails.country === "string"
                ? newDetails.country
                : newDetails.country.code;
            for (let step = 0; step < this.allowedCountries.length; step++) {
              if (this.allowedCountries[step].code === countryCode) {
                newDetails.country = this.allowedCountries[step];
              }
            }
            const avsDetailsChecked = await avsCheck(newDetails);
            this.$emit("emitUserDetails", avsDetailsChecked);
            // this if should be check if necessary split the address or not
            if (
              this.place &&
              this.location !== this.place.formatted_address &&
              !this.hasAdvancedInStep
            ) {
              this.place.getFormattedAddress(avsDetailsChecked.stageDetails);

              this.location = this.place?.formatted_address
                ? this.place.formatted_address
                : this.location;
            } else if (this.hasAdvancedInStep) {
              this.splitAddress();
            }
          }
        } else if (resolvedLocation && resolvedLocation.forLocation) {
          const result =
            this.place.reverseResolvedLocationComponents(resolvedLocation);
          const details: Details = new Details();
          const allowedStates = mapState(this.states);
          details.setStage(this.place, this.allowedCountries, allowedStates);
          details.postalCode = resolvedLocation.postal_code;
          details.searchType = "partial";
          for (let step = 0; step < this.allowedCountries.length; step++) {
            if (this.allowedCountries[step].code === details.country.code) {
              details.country = this.allowedCountries[step];
            }
          }
          if (
            details.address1 &&
            details.postalCode &&
            details.state &&
            details.city &&
            details.country
          ) {
            details.searchType = "full";
            const avsDetailsChecked = await avsCheck(details);
            this.$emit("emitUserDetails", avsDetailsChecked);
          } else {
            this.$emit("emitUserDetails", {
              stageDetails: details,
              updatedDetails: details,
              needsAVSModal: false,
            });
          }
          // this if should be check if neccessary split the address or not

          if (
            this.place &&
            this.location !== this.place.formatted_address &&
            !this.hasAdvancedInStep
          ) {
            this.location = this.place
              ? (this.place.formatted_address as string)
              : this.location;
          } else if (this.hasAdvancedInStep) {
            this.splitAddress();
          }
        } else {
          const details: Details = new Details();
          const allowedStates = mapState(this.states);
          details.setStage(this.place, this.allowedCountries, allowedStates);
          const postalCodeComponent = this.place.address_components
            ? this.place.address_components.find((component) =>
                component.types.includes("postal_code")
              )
            : false;
          details.postalCode = postalCodeComponent
            ? postalCodeComponent.long_name
            : "";
          details.searchType = "partial";
          this.$emit("emitUserDetails", {
            stageDetails: details,
            updatedDetails: details,
            needsAVSModal: false,
          });
          this.place.formatLabel(false, resolvedLocation);
          if (!this.hasAdvancedInStep) {
            this.location = this.place
              ? (this.place.formatted_address as string)
              : this.location;
          } else {
            this.splitAddress();
          }
        }
        this.$emit("emitLocation", this.place);
        this.suggestions = [];
        this.processing = false;
        this.isLoadingInput = false;
        this.skipEmitCheck = false;
        this.searchInputValue = "";
      } catch (e) {
        this.processing = true;
        this.skipEmitCheck = false;
        this.isLoadingInput = false;
      }
    },
    async resolveUserLocation(userAddress): Promise<void> {
      this.processing = true;
      let details = new Details();
      try {
        await this.reverseAddressComponents(userAddress, false);
        this.$emit("emitLocation", this.place);
        details = new Details(this.reverseDetailsComponents(userAddress));
        details.searchType = "full";
        details.isSavedAddress = true;
        details.avsStatus.source = DetailsSource.savedAddress;
        const allowedStates = mapState(this.states);
        for (let step = 0; step < this.allowedCountries.length; step++) {
          if (
            this.allowedCountries[step].code === userAddress.countryCode ||
            this.allowedCountries[step].name === userAddress.country
          ) {
            details.country = this.allowedCountries[step];
          }
        }
        if (details?.country?.code && allowedStates?.[details.country.code]) {
          for (
            let step = 0;
            step < allowedStates[details.country.code].length;
            step++
          ) {
            if (
              userAddress.state ===
              allowedStates[details.country.code][step].stateprovcode
            ) {
              details.state = allowedStates[details.country.code][step];
            }
          }
        }
        if (!details.passedAvs) {
          const avsDetailsChecked = await avsCheck(details);
          this.$emit("emitUserDetails", avsDetailsChecked);
        } else {
          this.$emit("emitUserDetails", {
            stageDetails: details,
            updatedDetails: details,
            changedFlags: false,
          });
        }
        this.place.getFormattedAddress(details);
        // TODO: improve this
        if (this.hasAdvancedInStep) {
          this.location = details.streetAddress ?? details.address1;
          this.city = details.city;
          this.country = details.country;
          this.state = details.state;
          this.postalCode = details.postalCode;
        } else {
          this.location = this.place
            ? (this.place.formatted_address as string)
            : this.location;
        }
        this.isLoadingInput = false;
        this.processing = false;
        this.suggestions = [];
        this.searchInputValue = "";
      } catch (error) {
        console.log(error);
      }
    },
    async resolveEventLocation(userAddress): Promise<void> {
      this.processing = true;
      let details = new Details();
      try {
        await this.reverseAddressComponents(userAddress, false);
        this.$emit("emitLocation", this.place);
        details = new Details(this.reverseDetailsComponents(userAddress));
        details.searchType = "full";
        details.isEventAddress = true;
        details.passedAvs = true;
        details.avsStatus.source = DetailsSource.event;
        const allowedStates = mapState(this.states);
        for (let step = 0; step < this.allowedCountries.length; step++) {
          if (
            this.allowedCountries[step].code === userAddress.countryCode ||
            this.allowedCountries[step].name === userAddress.country
          ) {
            details.country = this.allowedCountries[step];
          }
        }
        if (details?.country?.code && allowedStates?.[details.country.code]) {
          for (
            let step = 0;
            step < allowedStates[details.country.code].length;
            step++
          ) {
            if (
              userAddress.state ===
              allowedStates[details.country.code][step].stateprovcode
            ) {
              details.state = allowedStates[details.country.code][step];
            }
          }
        }
        this.$emit("emitUserDetails", {
          stageDetails: details,
          updatedDetails: details,
          needsAVSModal: false,
        });
        // TODO: improve this
        if (this.hasAdvancedInStep) {
          this.location = details.streetAddress ?? details.address1;
          this.city = details.city;
          this.country = details.country.name;
          this.state = details.state;
          this.postalCode = details.postalCode;
        } else {
          this.location = this.place
            ? (this.place.formatted_address as string)
            : this.location;
        }
        this.isLoadingInput = false;
        this.processing = false;
        this.suggestions = [];
      } catch (error) {
        console.log(error, "error");
      }
    },
    async reverseAddressComponents(
      userAddress,
      setAddress: boolean = true
    ): Promise<Place> {
      const _this = this;
      let address = "";
      if (userAddress) {
        if (userAddress.address1) {
          address += userAddress.address1;
        }
        if (userAddress.city) {
          address += (userAddress.address1 ? " " : "") + userAddress.city;
        }
        if (userAddress.state) {
          address += (userAddress.city ? ", " : "") + userAddress.state;
        }
        if (userAddress.postalCode) {
          address += (userAddress.state ? " " : "") + userAddress.postalCode;
        }
        if (userAddress.country) {
          address += (userAddress.postalCode ? ", " : "") + userAddress.country;
        }
      }
      if (setAddress) {
        this.location = address;
      }
      const autocompleteService = new google.maps.places.AutocompleteService();
      const predictions = await new Promise<any>((resolve, reject) => {
        autocompleteService.getPlacePredictions(
          { input: address },
          (predictions, status) => {
            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !predictions
            ) {
              reject(new Error("Failed to get predictions or status not OK."));
            }
            resolve(predictions);
          }
        );
      });
      const autocompleteOptions = {
        fields: [
          "formatted_address",
          "geometry",
          "name",
          "address_components",
          "vicinity",
          "type",
        ],
        strictBounds: false,
        placeId: predictions[0].place_id,
      };
      this.place = await this.getPlaceDetails(autocompleteOptions);

      _this.disabled = false;
      // this.place = this.autocomplete.getPlace() as Place;
      return this.place;
    },
    reverseDetailsComponents(userAddress): Details {
      const details = new Details();
      if (userAddress) {
        if (userAddress.companyName || userAddress.company)
          details.company = userAddress.companyName
            ? userAddress.companyName
            : userAddress.company;
        if (userAddress.firstName) details.firstName = userAddress.firstName;
        if (userAddress.lastName) details.lastName = userAddress.lastName;
        if (userAddress.streetAddress)
          details.streetAddress = userAddress.streetAddress;
        if (userAddress.address1) details.address1 = userAddress.address1;
        details.streetAddress = !details.streetAddress
          ? details.address1
          : details.streetAddress;

        if (userAddress.address2) details.address2 = userAddress.address2;
        if (userAddress.address3) details.address3 = userAddress.address3;
        if (userAddress.city) details.city = userAddress.city;
        if (userAddress.phone) details.phone = userAddress.phone;
        if (userAddress.country) {
          const countryName =
            typeof userAddress.country === "string"
              ? userAddress.country.trim().toLowerCase()
              : userAddress.country.name.trim().toLowerCase();
          const foundCountry = this.allowedCountries.find((country) => {
            const countryCode = String(country.code).trim().toLowerCase();
            const countryFullName = String(country.name).trim().toLowerCase();
            return (
              countryCode === countryName || countryFullName === countryName
            );
          });
          if (foundCountry) {
            details.country = foundCountry as Country;
          } else {
            details.country = userAddress.country;
          }
          this.filterStates = mapState(this.states);
        }
        if (userAddress.state) {
          if (typeof userAddress.state === "string") {
            if (this.filterStates[details.country.code]) {
              this.allowedState = this.filterStates[details.country.code];
              const foundAddress = this.allowedState.find((address) =>
                Object.values(address).includes(userAddress.state)
              );
              details.state = foundAddress;
            } else {
              details.state = userAddress.state;
            }
          } else {
            details.state = userAddress.state;
          }
        }
        details.passedAvs = userAddress.passedAvs;
        details.avsStatus.status = userAddress.passedAvs
          ? AVSStatus.success
          : AVSStatus.failed;
        if (userAddress.postalCode) details.postalCode = userAddress.postalCode;
        if (userAddress.addressId) details.addressId = userAddress.addressId;
        if (userAddress.displayName)
          details.displayName = userAddress.displayName;
      }
      return details;
    },
    splitAddress(): void {
      // TODO: move to utils folder
      if (!this.$store.state.ship.stages.details) {
        this.place = new Place(
          this.$store.state.ship.stages.trips[this.id][this.direction]
        );
        const streetNumber = this.place.getAddressProperty("street_number");
        const route = this.place.getAddressProperty("route");
        this.location = route.includes(streetNumber)
          ? route
          : streetNumber + " " + route;
        this.unit = this.place.getAddressProperty("unit");
        this.postalCode = this.place.getAddressProperty("postal_code");
        this.city = this.place.getAddressProperty("locality");
        this.state = this.place.getAddressProperty(
          "administrative_area_level_1"
        );
        this.country = this.place.getAddressProperty("country");
        this.isSingle = false;
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
      } else if (this.$store.state.ship.stages.details) {
        const details = new Details(
          this.$store.state.ship.stages.details?.[this.id]?.[this.direction]
        );
        this.location = details.address1
          ? details.address1
          : details.streetAddress;
        this.unit = details.address2;
        this.postalCode = details.postalCode;
        this.city = details.city;
        this.state =
          typeof details.state === "string"
            ? {
                id: details.state,
                label: details.state,
                code: details.state,
                stateprovcode: details.state,
              }
            : details.state;

        this.country = details.country;
        this.isSingle = false;
        this.isAddressExpanded = true;
        this.showArrowBtn = true;
        this.filterStates = mapState(this.states);
        this.allowedState = this.filterStates?.[this.country?.code];
      }
    },
    checkTrip() {
      setTimeout(() => {
        if (!this.skipEmitCheck) {
          this.$emit("emitCheckTrip", {
            id: this.id,
            direction: this.direction,
          });
        }
      }, 200);
    },
    async editAddress($event, element): Promise<void> {
      await new Promise((resolve) => setTimeout(resolve, 200));
      // Note: the promise is used to allow time for the @click functions to execute first
      // so by editing the flag skipEmitCheck and can check if necessary skip the emit
      if (!this.skipEmitCheck) {
        this.$emit("emitChangeAddress", {
          id: this.id,
          direction: this.direction,
          element,
          value: $event.target.value,
        });
      }
    },
    async getPlaceDetails(autocompleteOptions): Promise<Place> {
      const service = new this.googleObj.maps.places.PlacesService(
        document.createElement("div")
      );
      return await new Promise((resolve, reject) => {
        service.getDetails(autocompleteOptions, (place, status) => {
          if (status === this.googleObj.maps.places.PlacesServiceStatus.OK) {
            resolve(new Place(place));
          } else {
            reject(
              new Error(`Failed to get place details with status: ${status}`)
            );
          }
        });
      });
    },
    fetchSuggestions() {
      if (!this.$refs?.input) {
        return;
      }
      const input = (this.$refs.input as HTMLInputElement)?.value
        ? (this.$refs.input as HTMLInputElement).value
            .toLowerCase()
            .replace(/[,.]/g, "")
        : "";

      if (input.length < 3) {
        this.filteredUserAddress = this.userAddresses;
        this.suggestions = [];
        this.isLimit = false;
        return;
      }
      this.isLimit = true;
      this.filteredUserAddress = this.userAddresses.filter((address) => {
        const addressString = `${address.address1} ${address.city} ${
          address.state
        } ${address.postalCode} ${address.companyName || ""}`.toLowerCase();
        return (
          input.split(" ").every((term) => addressString.includes(term)) ||
          address?.displayName?.includes(input)
        );
      });
      const autocompleteService = new google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        { input },
        (predictions, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            this.suggestions = [];
            return;
          }
          this.suggestions = predictions;
        }
      );
    },
    selectSuggestion(suggestion) {
      this.skipEmitCheck = true;
      this.isLoadingInput = true;
      this.needSetAddress = false;
      this.processing = true;
      const autocompleteOptions = {
        fields: [
          "formatted_address",
          "geometry",
          "name",
          "address_components",
          "vicinity",
          "type",
        ],
        strictBounds: false,
        placeId: suggestion.place_id,
      };
      const service = new this.googleObj.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails(autocompleteOptions, (place, status) => {
        if (status === this.googleObj.maps.places.PlacesServiceStatus.OK) {
          setTimeout(() => {
            this.place = new Place(place);
            this.ResolveLocation();
          }, 200);
        }
      });
      this.suggestions = [];
      this.isOpenSuggestions = false;
    },
    navigateSuggestions(direction) {
      const itemListLength =
        this.suggestions.length + this.filteredUserAddress.length;
      if (direction === "down") {
        if (this.highlightedIndex < itemListLength - 1) {
          this.highlightedIndex++;
        } else {
          this.highlightedIndex = 0;
        }
      } else if (direction === "up") {
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
        } else {
          this.highlightedIndex = itemListLength - 1;
        }
      }
    },
    selectHighlightedSuggestion() {
      if (
        this.highlightedIndex >= 0 &&
        this.suggestions[this.highlightedIndex]
      ) {
        this.selectSuggestion(this.suggestions[this.highlightedIndex]);
        this.highlightedIndex = -1;
      }
    },
    async pasteAddress(event) {
      try {
        this.skipEmitCheck = true;
        this.isLoadingInput = true;
        const autocompleteService =
          new google.maps.places.AutocompleteService();
        const predictions = await new Promise<any>((resolve, reject) => {
          autocompleteService.getPlacePredictions(
            { input: event.clipboardData.getData("text").trim() },
            (predictions, status) => {
              if (
                status !== google.maps.places.PlacesServiceStatus.OK ||
                !predictions
              ) {
                reject(
                  new Error("Failed to get predictions or status not OK.")
                );
              }
              resolve(predictions);
            }
          );
        });
        const autocompleteOptions = {
          fields: [
            "formatted_address",
            "geometry",
            "name",
            "address_components",
            "vicinity",
            "type",
          ],
          strictBounds: false,
          placeId: predictions[0].place_id,
        };
        this.closeSuggestions();
        this.place = await this.getPlaceDetails(autocompleteOptions);
        await this.ResolveLocation();
      } catch (error) {
        this.skipEmitCheck = false;
        this.isLoadingInput = false;
        this.checkTrip();
      }
    },
    openSuggestionBox() {
      this.filteredUserAddress = this.userAddresses;
      this.isOpenSuggestions = true;
      if (this.location) {
        this.searchInputValue = this.location;
      }
      setTimeout(() => {
        if (this.location) {
          this.fetchSuggestions();
        }
        if (this.$refs?.input) {
          (this.$refs.input as HTMLInputElement).focus();
        }
      }, 500);
    },
    closeSuggestions() {
      this.isOpenSuggestions = false;
      this.suggestions = [];
      this.highlightedIndex = -1;
      this.searchInputValue = "";
      this.skipEmitCheck = false;
    },
    checkAddress() {
      setTimeout(() => {
        if (this.needSetAddress) {
          this.location = this.searchInputValue;
        }
      }, 200);
    },
    clearInput() {
      this.skipEmitCheck = true;
      this.closeSuggestions();
      this.location = "";
      this.state = { id: "", label: "-" };
      this.city = "";
      this.country = { id: "", label: "-" };
      this.postalCode = "";
      this.unit = "";
      this.$emit("emitClearInput");
    },
    countrySelectionMade(selection: any) {
      const elements = selection.name.split("-");
      if (elements.length > 1) {
        // if difference is found
        if (
          selection.option &&
          this.country &&
          selection.option.code !== this.country.code
        ) {
          this.state = { id: "", label: "" };
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "state",
            value: { id: "", label: "" },
          });
          this.country = selection.option;
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "country",
            value: selection.option,
          });
        }
      }
    },
    stateSelectionMade(selection: any) {
      const elements = selection.name.split("-");
      if (elements.length > 1) {
        // if difference is found
        if (
          selection.option &&
          this.state &&
          selection.option.stateprovcode !== this.state?.stateprovcode
        ) {
          this.state = selection.option;
          this.$emit("emitChangeAddress", {
            id: this.id,
            direction: this.direction,
            element: "state",
            value: selection.option,
          });
        }
        this.state = selection.option;
      }
    },
    parseStateLabel,
  },
});
